import { emptyIfInvalid } from '../../../utils/value.js'
import type * as Style from '../../index.js'
import {
  ColorReference,
  Float,
  Gradient,
  GradientColorStop,
  Keyword,
  Length,
  LengthOrPosition
} from '../../values/index.js'
import { fixReference as fixColorReference } from '../basic/color.js'
import { Set } from '../../index.js'

// Color/Gradient fill
export const Color = (props: any = {}) => ({
  backgroundColor: ColorReference(props.backgroundColor),
  backgroundGradient: Gradient(props.backgroundGradient)
})

Color.isValid = ({ backgroundColor, backgroundGradient }: Color) =>
  'red' in backgroundColor || ColorReference.isValid(backgroundColor) || Gradient.isValid(backgroundGradient)
export type Color = ReturnType<typeof Color>

// CSS effects
export const Effects = (props: any = {}) => ({
  blur: Length(props.blur),
  opacity: Float(props.opacity) ?? 1
})

Effects.isValid = ({ blur, opacity }: Effects) => blur.value != 0 || opacity != 1
export type Effects = ReturnType<typeof Effects>

// Background images
export enum BackgroundRepeat {
  'repeat' = 'repeat',
  'no-repeat' = 'no-repeat',
  'repeat-x' = 'repeat-x',
  'repeat-y' = 'repeat-y'
}

export enum BackgroundSize {
  'cover' = 'cover',
  'contain' = 'contain'
}

export const Image = (props: any = {}) => ({
  backgroundImage: props.backgroundImage ?? '',
  backgroundPositionX: LengthOrPosition(props.backgroundPositionX),
  backgroundPositionY: LengthOrPosition(props.backgroundPositionY),
  backgroundRepeat: Keyword<BackgroundRepeat>(props.backgroundRepeat, BackgroundRepeat, 'repeat'),
  backgroundSize: Keyword<BackgroundSize>(props.backgroundSize, BackgroundSize, 'cover')
})

Image.isValid = ({ backgroundImage }: Image) => backgroundImage != null
export type Image = ReturnType<typeof Image>

export const Props = (props: any = {}) => ({
  ...emptyIfInvalid(props, Color),
  ...emptyIfInvalid(props, Effects),
  ...emptyIfInvalid(props, Image)
})
export type Props = ReturnType<typeof Props>
export const fix = (props: Props, styles: Style.Rule[]): Props => ({
  ...props,
  backgroundColor: fixColorReference(props.backgroundColor, styles),
  backgroundGradient: {
    ...props.backgroundGradient,
    stops: props.backgroundGradient?.stops.map((stop: GradientColorStop) => ({
      ...stop,
      color: fixColorReference(stop.color as ColorReference, styles)
    }))
  }
})
export const validate = () => true
export const type = 'fill'
export const label = 'Fill'
export const category = 'aspect'
export const property = 'fillIds'
export const forms = [
  { id: 'fillColor', label: 'Color', extractProperties: (props: Props) => Color(props), validate: Color.isValid },
  {
    id: 'fillEffects',
    label: 'Effects',
    extractProperties: (props: Props) => Effects(props),
    validate: Effects.isValid
  }
]
export const summarize = (props: Props, styles?: Style.Rule[], formId?: string) => {
  const color = Set.findByTypeAndId(styles || [], 'color', props.backgroundColor.id)
  switch (formId) {
    case 'fillEffects':
      const effects = []
      if (props.blur.value > 0) effects.push('blur')
      if (props.opacity < 1) effects.push('opacity')
      return effects.join(' / ') || 'None'
    case 'fillColor':
      return `${
        props.backgroundGradient.stops.length
          ? 'Gradient'
          : color
          ? `rgba(${color.props.red}, ${color.props.green}, ${color.props.blue}, ${color.props.alpha})`
          : 'None'
      }`
    default:
      return `${props.backgroundGradient.stops.length ? 'Gradient' : color?.details.title || 'None'}`
  }
}
